/**
 *
 * CustomForm
 *
 */

import React, { memo, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { split } from "ramda";
import { useTranslation } from "react-i18next";

import "./style.scss";

import { Form, Button, Col, Row, Select } from "antd";
const { Option, OptGroup } = Select;

// utils
import {
  prepearFormValue,
  prepearFormValueRemoveHiddenChain,
} from "utils/forms/forms.utils";

// components
import DateField from "components/Forms/Fields/DateField";
import TextField from "components/Forms/Fields/TextField";
import TextareaField from "components/Forms/Fields/TextareaField";
import HiddenField from "components/Forms/Fields/HiddenField";
import NumberField from "components/Forms/Fields/NumberField";
import PhoneField from "components/Forms/Fields/PhoneField";
import EmailField from "components/Forms/Fields/EmailField";
import EmailValidatedInput from "components/Forms/Fields/EmailValidatedInput";
import PasswordField from "components/Forms/Fields/PasswordField";
import TimeField from "components/Forms/Fields/TimeField";

import DynamicMultipleSelect from "components/Forms/Fields/DynamicMultipleSelect";
import RichEditorField from "components/Forms/Fields/RichEditorField";

// containers
import SelectFieldAxios from "containers/SelectFieldAxios";

function CustomForm({
  mode,
  method,
  form,
  initialValues,
  name,
  dataSource,
  labelCol,
  wrapperCol,
  disabeled,
  hideSubmit,
  // actions
  handleSubmit,
  handleChange,
  handleValuesChange,
  // default
  className,
  ...rest
}) {
  const childClassNames = classNames("custom-form-wrapper", className);
  const { t, i18n } = useTranslation();

  const onFinish = values => {
    const sendingData = prepearFormValue(dataSource, values);
    const sendingDataClear = prepearFormValueRemoveHiddenChain(
      dataSource,
      sendingData,
    );
    handleSubmit(sendingDataClear);
  };

  const onFinishFailed = errorInfo => {
    console.error("Failed CustomForm:", errorInfo);
  };

  const selectField = useCallback((item, index) => {
    return (
      <Col
        key={item.name + index + "-col"}
        md={item.col || 24}
        xs={24}
        offset={item.offset || null}
      >
        <Form.Item
          key={item.name + index + "-item"}
          label={item?.displayLabel ? item.label : item.label}
          name={item.name}
          className={item.name + "-wrapper"}
          // initialValue={item.value}
          // optionFilterProp="children"
          rules={[
            {
              required: item.required || false,
              message: t("inputs.mandatoryField")
            },
          ]}
          onSelect={(LabeledValue, option) => {
            // console.log({ LabeledValue, option });
          }}
        >
          <Select
            placeholder={t("inputs.select")}
            getPopupContainer={trigger => trigger.parentElement}
          >
            {item.selectContent.map((option, i) => {
              if (option?.group) {
                return (
                  <OptGroup
                    key={option?.group || option?.key}
                    label={option?.label || option?.group || option?.key}
                  >
                    {option?.options?.map((option, i) => (
                      <Option
                        key={option?.key || option?.value}
                        value={option?.value || option?.key}
                        color={option?.color}
                      >
                        {option?.label || option?.value}
                      </Option>
                    ))}
                  </OptGroup>
                );
              } else {
                return (
                  <Option
                    key={option?.key || option?.value}
                    value={option?.value || option?.key}
                    color={option?.color}
                  >
                    {option?.label || option?.value}
                  </Option>
                );
              }
            })}
          </Select>
        </Form.Item>
      </Col>
    );
  },[i18n?.language, dataSource]);

  const selectFieldGroup = (item, index) => {
    return (
      <Col
        key={item.name + index + "-col"}
        md={item.col || 24}
        xs={24}
        offset={item.offset || null}
      >
        <Form.Item
          key={item.name + index + "-item"}
          label={item?.displayLabel ? item.label : item.label}
          name={item.name}
          className={item.name + "-wrapper"}
          // initialValue={item.value}
          // optionFilterProp="children"
          rules={[
            {
              required: item.required || false,
              message: t("inputs.mandatoryField")
            },
          ]}
          onSelect={(LabeledValue, option) => {
            // console.log({ LabeledValue, option });
          }}
        >
          <Select
            placeholder={t("inputs.select")}
            optionFilterProp="children"
            getPopupContainer={trigger => trigger.parentElement}
          >
            {item.selectContent.map((option, i) => {
              if (option?.group) {
                return (
                  <OptGroup
                    key={option?.group || option?.key}
                    label={option?.label || option?.group || option?.key}
                  >
                    {option?.options?.map((option, i) => (
                      <Option
                        key={option?.key || option?.value}
                        value={option?.value || option?.key}
                        color={option?.color}
                      >
                        {option?.label || option?.value}
                      </Option>
                    ))}
                  </OptGroup>
                );
              } else {
                return (
                  <Option
                    key={option?.key || option?.value}
                    value={option?.value || option?.key}
                    color={option?.color}
                  >
                    {option?.label || option?.value}
                  </Option>
                );
              }
            })}
          </Select>
        </Form.Item>
      </Col>
    );
  };

  const selectSaveToField = (item, index) => {
    return (
      <Col
        key={item.name + index + "-col"}
        md={item.col || 24}
        xs={24}
        offset={item.offset || null}
      >
        <Form.Item
          key={item.name + index + "-item"}
          label={item?.displayLabel ? item.label : item.label}
          name={item.name}
          className={item.name + "-wrapper"}
          rules={[
            {
              required: item.required || false,
              message: t("inputs.mandatoryField"),
              validateTrigger: "onSubmit",
            },
          ]}
        >
          <Select
            placeholder={t("inputs.select")}
            optionFilterProp="children"
            onSelect={(LabeledValue, option, all) => {
              if (item.chain.indexOf(".") != -1) {
                // Split by .
                const chainPath = split(".", item.chain);
                if (option[chainPath[1]]) {
                  form.setFieldsValue({
                    [item.chain]: option[chainPath[1]],
                  });
                }
              } else {
                if (option[item.chain]) {
                  form.setFieldsValue({
                    [item.chain]: option[item.chain],
                  });
                }
              }
            }}
            getPopupContainer={trigger => trigger.parentElement}
          >
            {item?.selectContent?.map((option, i) => (
              <Option
                key={option?.key || option?.value}
                value={option?.value || option?.key}
                color={option?.color}
              >
                {option?.label || option?.value}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    );
  };

  const axiosSelectField = useCallback((item, index) => {
    return (
      <Col
        key={item.name + index + "-col"}
        md={item.col || 24}
        xs={24}
        offset={item.offset || null}
      >
        <SelectFieldAxios
          mode={item?.mode}
          sortBy={item?.sortBy}
          endponit={item.endponit}
          itemCallback={item.itemCallback}
          item={item}
        />
      </Col>
    );
  },[i18n?.language, dataSource]);

  const renderInputType = useCallback(
    children => {
      return (
        children &&
        children?.map((item, index) => {
          const onlyRead = mode == "onlyRead" && item?.hasModeView;
          const isEdit = mode == "edit" || mode == "new";

          switch (item.type) {
            case "input":
              return (
                <TextField key={`input-${index}`} data={item} index={index} />
              );
            case "email":
              return (
                <EmailField key={`email-${index}`} data={item} index={index} />
              );
            case "email-validate":
              return (
                <EmailValidatedInput
                  key={`email-validate-${index}`}
                  data={item}
                  index={index}
                  method={method}
                />
              );
            case "phone":
              return (
                <PhoneField key={`phone-${index}`} data={item} index={index} />
              );
            case "textarea":
              return (
                <TextareaField
                  key={`textarea-${index}`}
                  data={item}
                  index={index}
                  rows={4}
                />
              );
            case "select":
              return selectField(item, index);
            case "select-group":
              return selectFieldGroup(item, index);
            case "hidden":
              return (
                <HiddenField
                  key={`hidden-${index}`}
                  data={item}
                  index={index}
                />
              );
            case "number":
              return (
                <NumberField
                  key={`number-${index}`}
                  data={item}
                  index={index}
                />
              );
            case "submit":
              return (
                !!!hideSubmit && (
                  <Form.Item
                    key={item.name + index + "-item"}
                    className="submit-wrapper"
                  >
                    <Button
                      type="primary"
                      className={`${disabeled ? "disabeled" : ""}`}
                      htmlType="submit"
                      size={"large"}
                    >
                      {t("common.save")}
                    </Button>
                  </Form.Item>
                )
              );

            case "select-chain":
              return selectSaveToField(item, index);

            case "time-range":
              return (
                <TimeField
                  key={`time-range-${index}`}
                  data={item}
                  index={index}
                />
              );

            case "date":
              return (
                (isEdit || onlyRead) && (
                  <DateField
                    key={`date-${index}`}
                    data={item}
                    index={index}
                    onlyRead={onlyRead}
                    className={`${onlyRead ? " only-read" : ""}`}
                  />
                )
              );

            case "axios-select":
              return axiosSelectField(item, index);

            case "axios-select-multiple":
              return (
                <DynamicMultipleSelect
                  key={`axios-select-multiple-${index}`}
                  mode={mode}
                  method={method}
                  onlyRead={onlyRead}
                  form={form}
                  data={item}
                  index={index}
                />
              );

            case "password":
              return (
                <PasswordField
                  key={`password-${index}`}
                  data={item}
                  index={index}
                  method={method}
                />
              );

            case "rich-editor":
              return (
                <RichEditorField
                  key={`rich-editor-${index}`}
                  mode={mode}
                  form={form}
                  data={item}
                  index={index}
                  method={method}
                />
              );
          }
        })
      );
    },
    [dataSource, disabeled, mode, form, method, name, i18n?.language],
  );

  const onValuesChangeCallback = useCallback((changedValues, allValues) => {
    // Email has to be only lowercase on creation and without accents
    if (!!changedValues?.email) {
      let newEmail = changedValues?.email.toLowerCase();
      newEmail = newEmail.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      form.setFieldsValue({
        email: newEmail,
      });
    }
    !!handleValuesChange && handleValuesChange(changedValues, allValues);
  }, [i18n?.language]);

  return (
    <div className={childClassNames}>
      <Form
        form={form}
        initialValues={initialValues || {}}
        name={name}
        className="custom-form"
        labelCol={{ span: labelCol || 8 }}
        wrapperCol={{ span: wrapperCol || 16 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onFieldsChange={handleChange}
        onValuesChange={onValuesChangeCallback}
        autoComplete="off"
      >
        {dataSource?.map(
          item =>
            item && (
              <Row key={item?.key + "-row-main"}>
                {item?.parentLabel && (
                  <Col key={item?.key + "-col-main"} span={24}>
                    <h4 className="ant-form-item-title">{item?.parentLabel}</h4>
                  </Col>
                )}
                {renderInputType(item?.children)}
              </Row>
            ),
        )}
      </Form>
    </div>
  );
}

CustomForm.defaultProps = {
  disabeled: false,
  method: "update",
  mode: "update",
  name: "basic",
  hideSubmit: false,
};

CustomForm.propTypes = {
  disabeled: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
  ]),
};

export default memo(CustomForm);
